<template>
  <div>
    <p>
      Product ID <strong>{{ iaps.sku || '…' }}</strong>, price <strong>{{ iaps.price || 'none' }}</strong>
      (the price comes from the website product).
      <b-button
        size="sm"
        variant="outline-primary"
        class="ml-1"
        :disabled="busy"
        @click="load"
      >
        Refresh
      </b-button>
    </p>

    <b-alert
      :show="!!working"
      variant="info"
      class="d-flex align-items-center"
    >
      <b-spinner
        small
        class="mr-1"
      />
      {{ working }}
    </b-alert>

    <b-row>
      <!-- App Store -->
      <b-col md="6">
        <h5>
          App Store
          <b-badge
            pill
            :variant="appleVariant"
          >
            {{ appleStatus }}
          </b-badge>
        </h5>
        <b-form-group :label="`Name (${appleName.length}/${iaps.appleNameLimit})`">
          <b-form-input
            v-model="appleName"
            :state="appleName.length <= iaps.appleNameLimit ? null : false"
          />
        </b-form-group>
        <b-form-group :label="`Description (${appleDescription.length}/${iaps.appleDescriptionLimit})`">
          <b-form-input
            v-model="appleDescription"
            :state="appleDescription.length <= iaps.appleDescriptionLimit ? null : false"
          />
        </b-form-group>
        <b-button
          variant="primary"
          class="mr-1"
          :disabled="busy || !canCreateApple"
          @click="createApple"
        >
          {{ iaps.apple ? 'Complete on App Store' : 'Create on App Store' }}
        </b-button>
        <b-button
          variant="success"
          :disabled="busy || appleStatus !== 'READY_TO_SUBMIT'"
          @click="submitApple"
        >
          Submit for Review
        </b-button>
        <p class="text-muted small mt-1">
          Uses the shared review screenshot. After creating, Apple needs a few minutes to process it;
          the status updates by itself until it's READY_TO_SUBMIT, then submit.
        </p>
      </b-col>

      <!-- Google Play -->
      <b-col md="6">
        <h5>
          Google Play
          <b-badge
            pill
            :variant="androidVariant"
          >
            {{ androidStatus }}
          </b-badge>
        </h5>
        <b-form-group :label="`Name (${androidName.length}/${iaps.androidNameLimit})`">
          <b-form-input
            v-model="androidName"
            :state="androidName.length <= iaps.androidNameLimit ? null : false"
          />
        </b-form-group>
        <b-form-group :label="`Description (${androidDescription.length}/${iaps.androidDescriptionLimit})`">
          <b-form-input
            v-model="androidDescription"
            :state="androidDescription.length <= iaps.androidDescriptionLimit ? null : false"
          />
        </b-form-group>
        <b-button
          variant="primary"
          :disabled="busy || !!iaps.android || !androidValid"
          @click="createAndroid"
        >
          Create on Google Play
        </b-button>
        <p class="text-muted small mt-1">
          Google Play products are live as soon as they are created; there is no review.
        </p>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import {
  BRow, BCol, BFormGroup, BFormInput, BButton, BBadge, BAlert, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

// Apple states after which creating again makes no sense
const APPLE_DONE = ['READY_TO_SUBMIT', 'WAITING_FOR_REVIEW', 'IN_REVIEW', 'PENDING_BINARY_APPROVAL', 'APPROVED']

export default {
  components: {
    BRow, BCol, BFormGroup, BFormInput, BButton, BBadge, BAlert, BSpinner,
  },
  props: {
    book: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      busy: false,
      // what's running, shown with a spinner (store calls can take a minute)
      working: '',
      // re-checks Apple after a create, until it has processed the screenshot
      pollTimer: null,
      iaps: {
        appleNameLimit: 30, appleDescriptionLimit: 45, androidNameLimit: 55, androidDescriptionLimit: 200,
      },
      appleName: '',
      appleDescription: '',
      androidName: '',
      androidDescription: '',
    }
  },
  computed: {
    appleStatus() {
      return this.iaps.apple ? this.iaps.apple.status : 'NOT CREATED'
    },
    androidStatus() {
      return this.iaps.android ? this.iaps.android.status : 'NOT CREATED'
    },
    appleVariant() {
      if (this.appleStatus === 'APPROVED') return 'success'
      if (['WAITING_FOR_REVIEW', 'IN_REVIEW'].includes(this.appleStatus)) return 'info'
      if (this.appleStatus === 'READY_TO_SUBMIT') return 'primary'
      if (['REJECTED', 'DEVELOPER_ACTION_NEEDED'].includes(this.appleStatus)) return 'danger'
      return this.iaps.apple ? 'warning' : 'secondary'
    },
    androidVariant() {
      if (this.androidStatus === 'active') return 'success'
      return this.iaps.android ? 'warning' : 'secondary'
    },
    canCreateApple() {
      return !APPLE_DONE.includes(this.appleStatus)
        && this.appleName && this.appleName.length <= this.iaps.appleNameLimit
        && this.appleDescription && this.appleDescription.length <= this.iaps.appleDescriptionLimit
    },
    androidValid() {
      return this.androidName && this.androidName.length <= this.iaps.androidNameLimit
        && this.androidDescription && this.androidDescription.length <= this.iaps.androidDescriptionLimit
    },
  },
  mounted() {
    this.fillDefaults()
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.pollTimer)
  },
  methods: {
    fillDefaults() {
      const title = this.book.title || ''
      const byAuthor = this.book.author ? `by ${this.book.author}` : ''
      this.appleName = title.substring(0, this.iaps.appleNameLimit)
      this.appleDescription = byAuthor.substring(0, this.iaps.appleDescriptionLimit)
      this.androidName = title.substring(0, this.iaps.androidNameLimit)
      this.androidDescription = `${title} ${byAuthor}`.trim().substring(0, this.iaps.androidDescriptionLimit)
    },
    call(method, path, body) {
      const url = `${this.$readerURL}/admin/books/${this.book.id}/iaps${path}`
      return firebase.auth().currentUser.getIdToken(false).then(idToken => axios({
        method, url, data: body, headers: { Authorization: idToken },
      }))
    },
    run(action, successTitle, workingText) {
      this.busy = true
      this.working = workingText
      return action()
        .then(() => {
          this.toast(successTitle, 'success')
          return this.load()
        })
        .catch(error => {
          const reason = error.response && error.response.data && error.response.data.errors
          this.$bvModal.msgBoxOk(reason ? `${reason}` : `${error}`, { title: 'Failed', centered: true })
        })
        .finally(() => {
          this.busy = false
          this.working = ''
        })
    },
    load() {
      this.busy = true
      this.working = this.working || 'Checking the App Store and Google Play…'
      return this.call('get', '')
        .then(response => { this.iaps = response.data })
        .catch(error => this.toast(`Could not load IAPs: ${error}`, 'danger'))
        .finally(() => {
          this.busy = false
          this.working = ''
        })
    },
    confirm(message) {
      return this.$bvModal.msgBoxConfirm(message, { title: 'Please confirm', centered: true, okTitle: 'Yes' })
    },
    createApple() {
      this.confirm(`Create ${this.iaps.sku} on the App Store at ${this.iaps.price}? This is the live App Store.`)
        .then(ok => ok && this.run(
          () => this.call('post', '/apple', { name: this.appleName, description: this.appleDescription }),
          'App Store IAP created',
          'Creating on the App Store: product, availability, price, listing and review screenshot. This can take a minute…',
        ).then(() => this.waitForApple(20)))
    },
    // Apple reports MISSING_METADATA until the screenshot is processed: check every 15 s, quietly
    waitForApple(triesLeft) {
      clearTimeout(this.pollTimer)
      if (this.appleStatus !== 'MISSING_METADATA' || triesLeft <= 0) return
      this.pollTimer = setTimeout(() => {
        this.call('get', '')
          .then(response => { this.iaps = response.data })
          .catch(() => {})
          .finally(() => this.waitForApple(triesLeft - 1))
      }, 15000)
    },
    submitApple() {
      this.confirm(`Submit ${this.iaps.sku} to Apple for review?`)
        .then(ok => ok && this.run(() => this.call('post', '/apple/submit'), 'Submitted for review', 'Submitting to Apple for review…'))
    },
    createAndroid() {
      this.confirm(`Create ${this.iaps.sku} on Google Play at ${this.iaps.price}? It goes live immediately.`)
        .then(ok => ok && this.run(
          () => this.call('post', '/android', { name: this.androidName, description: this.androidDescription }),
          'Google Play IAP created',
          'Creating on Google Play and activating it…',
        ))
    },
    toast(title, variant) {
      this.$toast({ component: ToastificationContent, props: { title, icon: 'EditIcon', variant } })
    },
  },
}
</script>
